const MMSuperboxUtils = {
  // Extract "Je m'abonne" or whatever the text is of "Je m'abonne (15.65€)" button's text, to ensure changing text will only be needed in html
  addToElementText(element, textToAdd) {
    const originalText = element.innerText.match(/([^(]+) ?/)[1].trim();
    element.innerText = `${originalText}${textToAdd}`;
  },
  addTargetingCapacity(klass, identifier) {
    const targetNames = klass.targets || [];
    targetNames.forEach(function(targetName) {
      const selector = `[data-mm-superbox-${identifier}-target="${targetName}"]`;
      Object.defineProperty(klass.prototype, `${targetName}Target`, {
        get() {
          return this.element.querySelector(selector);
        }
      });
      Object.defineProperty(klass.prototype, `${targetName}Targets`, {
        get() {
          return Array.from(this.element.querySelectorAll(selector));
        }
      });
    });
  },
  formatPrice(price) {
    return this._priceFormatter.format(price);
  },
  _priceFormatter: new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }),
  hideElement(element) {
    element.style.setProperty("display", "none", "important");
  },
  displayElement(element) {
    element.style.removeProperty("display");
  }
}

class MMSuperbox {
  constructor(needsData) {
    this.element = document.querySelector("[data-mm-superbox-container]");

    this.steps = [
      new MMSuperboxStepNeeds(this),
      new MMSuperboxStepInhabitants(this),
      new MMSuperboxStepQuestion(this),
      new MMSuperboxStepFragrances(this),
      new MMSuperboxStepProducts(this),
      new MMSuperboxStepContainers(this),
    ];
    this.stepsByName = {};
    this.steps.forEach((step, index) => {
      step.index = index;
      this.stepsByName[step.constructor.name] = step;
      MMSuperboxUtils.addTargetingCapacity(step.constructor, "step");
    });

    this.currentStepIndex = null;
    this.currentStep = null;
    this.editMode = false;
    this.needsByHandle = {};
    needsData.forEach(needData => this.needsByHandle[needData.handle] = new MMSuperboxNeed(this, needData));
    this.needs = Object.values(this.needsByHandle);

    this.productsById = {};
    this.needs.forEach((need) => {
      need.products.forEach(product => this.productsById[product.id] = product);
    });
    this.products = Object.values(this.productsById);
    this.cart = new MMSuperboxCart(this);
    this._monthFrequencyWas = this.selectedMonthFrequency;
  }

  get selectedNeeds () {
    return this.needs.filter(need => need.isSelected);
  }

  get selectedNeedsWithQuestion() {
    return this.selectedNeeds.filter(need => need.requiresQuestion);
  }

  get selectedFragrancesByCategory() {
    const fragrancesByCategory = {};
    this.step("fragrances").categoryTargets.map((categoryTarget) => {
      fragrancesByCategory[categoryTarget.dataset.categoryHandle] ||= []
      categoryTarget.querySelectorAll("input:checked").forEach((input) => {
        fragrancesByCategory[categoryTarget.dataset.categoryHandle].push(input.value);
      });
    });

    return fragrancesByCategory;
  }

  get requiresQuestion() {
    return this.selectedNeeds.some(need => need.requiresQuestion);
  }

  /*
    If any selected need has at least one product group with many products
    it means that there are several fragrances for this products and user
    must chose of them for each product.
  */
  get requiresFragrancesChoice() {
    return this.selectedNeeds.some((need) => {
      return need.productGroups && need.productGroups.some((productGroup) => {
        return productGroup.length > 2
      });
    });
  }

  get requiresContainers() {
    return this.products.some(product => product.requiresContainer);
  }

  get containers() {
    return this.step("containers").containers;
  }

  get progressBarInnerElement() {
    const progressBarElement = this.element.querySelector('[data-mm-superbox-progress-bar]');
    return progressBarElement.firstElementChild;
  }

  get currentStep() {
    if (this.currentStepIndex !== null) {
      return this.steps[this.currentStepIndex];
    }
    return null;
  }

  get backBtn() {
    return this.element.querySelector('[data-mm-superbox-back-btn');
  }

  get closeBtn() {
    return this.element.querySelector('[data-mm-superbox-close-btn');
  }

  findPreviousStep(currentStepIndex = this.currentStepIndex) {
    if (currentStepIndex === null) { return this.steps[0] }

    const previousStepIndex = currentStepIndex - 1;
    if (previousStepIndex < 0) { return null }

    const previousStep = this.steps[previousStepIndex];
    return previousStep.isRequired ? previousStep : this.findPreviousStep(previousStepIndex);
  }

  findNextStep(currentStepIndex = this.currentStepIndex) {
    if (currentStepIndex === null) { return this.steps[0] }

    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex >= this.steps.length) { return null }

    const nextStep = this.steps[nextStepIndex];
    return nextStep.isRequired ? nextStep : this.findNextStep(nextStepIndex);
  }

  get currentDiscountPercent() {
    return this.selectedNeeds.length * 5; // 5% per selected need, so 20% for 4 needs
  }

  get currentDiscountRatio() {
    return this.currentDiscountPercent / 100;
  }

  get currentDiscountCode() {
    return `SUPERBOX${this.currentDiscountPercent}`;
  }

  get selectedMonthFrequency() {
    return parseInt(this.step("products").monthFrequencyInputTarget.value);
  }

  get loaderHTML() {
    const loaderTemplate = this.element.querySelector('[data-mm-superbox-loader]');
    return loaderTemplate.innerHTML;
  }

  get loaderContainer() {
    return this.element.querySelector('[data-loader-container]');
  }

  set currentStep(step) {
    this.moveToStep(step);
  }

  displayLoader() {
    this.steps.forEach(step => step.hide());
    MMSuperboxUtils.displayElement(this.loaderContainer);
  }

  hideLoader() {
    MMSuperboxUtils.hideElement(this.loaderContainer);
  }

  moveToStep(step, { prepare = true } = {}) {
    if (step) {
      this.currentStepIndex = step.index;
      prepare && step.prepare();
      step.display();
      return this.updateProgressBar();
    }
    this.currentStepIndex = null;
    this.resetProgressBar();
  }

  resetProgressBar() {
    this.progressBarInnerElement.style.width = '0%';
  }

  updateProgressBar() {
    const stepsCount = this.steps.filter(step => step.isRequired).length;

    let pastSteps = 0;
    let step = this.step("needs");
    while(step) {
      pastSteps += 1;
      if(step === this.currentStep) { break }
      step = step.nextStep;
    }
    const widthPercent = (pastSteps / stepsCount) * 100;
    this.progressBarInnerElement.style.width = `${widthPercent}%`;
  }

  getInHabitantsCountByTypology() {
    return this.step("inhabitants").getInHabitantsCountByTypology();
  }

  findProductFromChildElement(element) {
    const productCard = element.closest('[data-mm-superbox-step-target="productCard"]');
    return this.productsById[productCard.dataset.productId];
  }

  findContainerFromChildElement(element) {
    const containerCard = element.closest('[data-mm-superbox-step-target="containerCard"]');
    return this.containers.find((container) => {
      return container.id === parseInt(containerCard.dataset.productId);
    });
  }

  step(stepName) {
    return this.stepsByName[stepName];
  }

  reset() {
    this.hideLoader();
    this.steps.forEach(step => step.reset());
    this.editMode = false;
    MMSuperboxUtils.displayElement(this.backBtn);
    MMSuperboxUtils.hideElement(this.closeBtn);
  }

  open() {
    this.reset();
    MMSuperboxUtils.displayElement(this.element);
    this.currentStep = this.step("needs");
  }

  async openForEdit() {
    this.reset();
    this.editMode = true;
    this.displayLoader();
    MMSuperboxUtils.displayElement(this.element);

    MMSuperboxUtils.displayElement(this.closeBtn);
    // MMSuperboxUtils.hideElement(this.backBtn);

    const cart = await this.cart.getCart() // cart
    const superboxState = JSON.parse(cart.attributes.superbox_state);
    const { answersByNeedHandle,
            inhabitantsCountByTypology,
            selectedFragrancesByCategory,
            selectedFrequency,
            selectedNeedHandles, } = superboxState;

    // display need products container
    selectedNeedHandles.forEach((needHandle) => {
      const need = this.needsByHandle[needHandle];
      MMSuperboxUtils.displayElement(need.productsContainer);
      need.checkbox.checked = true;
      need.products.forEach(product => product.hideCard());
    });
    this.step("needs").update(); // enable submit btn

    // if one of needs has intermediate questions, prepare question step
    if (this.requiresQuestion) {
      this.selectedNeedsWithQuestion.forEach((need) => {
        const answerHandle = answersByNeedHandle[need.handle];
        const answer = need.answers.find(answer => answer.handle === answerHandle);

        answer.choose();
      });
      const questionStep = this.step("question");
      questionStep.prepare(); // display need container
      questionStep.update(); // enable btn
    }

    // set inhabitant count by typology
    const inhabitantsStep = this.step("inhabitants");
    Object.keys(inhabitantsCountByTypology).forEach((typology) => {
      const count = inhabitantsCountByTypology[typology];
      inhabitantsStep.setInhabitantCount(typology, count);
    });
    inhabitantsStep.update(); // enable submit btn

    // set selected fragrances
    const fragrancesStep = this.step("fragrances");
    fragrancesStep.prepare(); // build and insert fragrance inputs
    Object.keys(selectedFragrancesByCategory).forEach((categoryHandle) => {
      const fragranceHandles = selectedFragrancesByCategory[categoryHandle];
      const categoryContainer = fragrancesStep.categoryTargets.find(e => e.dataset.categoryHandle === categoryHandle);

      categoryContainer.querySelectorAll("input").forEach((input) => {
        fragranceHandles.includes(input.value) && (input.checked = true);
      });
    });
    fragrancesStep.update(); // enable submit btn

    // set products quantity
    cart.items.forEach((item) => {
      if (item.properties) {
        if (item.properties.__superpack) {
          const product = this.products.find(product => product.variantId === item.variant_id);
          if (product) { // if product is not found, it should mean that it's a container
            product.quantity = item.quantity;
            product.applyDiscount(this.currentDiscountRatio);
            if (product.quantity > 0) { product.displayCard() }
          }
        }
      }
    });

    this.hideLoader();

    const productStep = this.step("products");
    productStep.monthFrequencyInputTarget.value = selectedFrequency;

    this.currentStep = productStep;
    this.currentStep.update(); // update price in btn
    this.updateProgressBar();
  }

  close() {
    MMSuperboxUtils.hideElement(this.element);
  }

  prepareCurrentStep() {
    this.currentStep.prepare();
  }

  updateCurrentStep() {
    this.currentStep.update();
  }

  submitCurrentStep() {
    this.currentStep.submit();
    if (this.currentStep.isLastStep) {
      return this.submit();
    }
    this.currentStep = this.currentStep.nextStep;
  }

  backToPreviousStep() {
    const previousStep = this.currentStep.previousStep;
    if (previousStep) {
      this.moveToStep(previousStep, { prepare: false });
    } else {
      this.close();
    }
  }

  async submit() {
    if (this.currentStep.submitBtnTarget) {
      this.currentStep.submitBtnTarget.disabled = true;
      this.currentStep.submitBtnTarget.innerHTML = this.loaderHTML;
    }
    await this.cart.clearExistingSuperbox();
    this.cart.addProductsToCart();
  }
}

class MMSuperboxNeed {
  static productKeys = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(n => `products_${n}`);

  constructor(superbox, needData) {
    this.superbox = superbox;
    this.needData = needData;
    this.handle = needData.handle;

    if (this.requiresQuestion) {
      this.questionText = needData.intermediate_question.question;
      this.questionHandle = needData.intermediate_question.handle;
      this.answers = needData.intermediate_question.answers.map((answerData) => {
        return new MMSuperboxAnswer(this, answerData);
      });
    } else {
      this.productGroups = [];
      this.constructor.productKeys.forEach((productKey) => {
        const products = needData.products[productKey];
        if (products) {
          const productGroup = products.map((productData) => {
            return new MMSuperboxProduct(this.superbox, productData);
          });
          this.productGroups.push(productGroup);
        }
      });
    }

    // clear duplicate products for each need
    const productCards = this.productsContainer.querySelectorAll('[data-mm-superbox-step-target="productCard"');
    const seenProductIDs = new Set();
    productCards.forEach((productCard) => {
      if (seenProductIDs.has(productCard.dataset.productId)) {
        return productCard.remove();
      }
      seenProductIDs.add(productCard.dataset.productId);
    });
  }

  get products() {
    if (this.requiresQuestion) {
      return this.answers.map(answer => answer.products).flat();
    }
    return this.productGroups.flat();
  }

  get isSelected() {
    return this.checkbox.checked;
  }

  get requiresQuestion() {
    return !!this.needData.intermediate_question;
  }

  get selectedAnswer() {
    if (this.requiresQuestion) {
      return this.answers.find(answer => answer.isSelected);
    }
  }

  get checkbox() {
    const needStep = this.superbox.step("needs");
    return needStep.needInputTargets.find((inputTarget) => {
      return inputTarget.value === this.handle;
    });
  }

  get productsContainer() {
    return this.superbox.step("products").needProductsContainerTargets.find(container => {
      return container.dataset.needHandle === this.handle;
    });
  }
}

class MMSuperboxAnswer {
  constructor(need, answerData) {
    this.need = need;
    this.handle = answerData.handle;
    this.text = answerData.answer;
    this.image = answerData.image;
    this.products = answerData.products.map((productData) => {
      return new MMSuperboxProduct(this.need.superbox, productData);
    });
  }

  get inputElement() {
    const questionStep = this.need.superbox.step("question");

    return questionStep.answerInputTargets.find(input => input.value === this.handle);
  }

  get isSelected() {
    return this.inputElement.checked;
  }

  choose() {
    this.inputElement.checked = true;
  }
}

class MMSuperboxBaseProduct {
  get quantityElement() {
    return this._quantityElement ||= this.card.querySelector('[data-item-quantity]');
  }

  get priceElement() {
    return this._priceElement ||= this.card.querySelector('[data-unit-price-cents]');
  }

  get fullPriceElement() {
    return this._fullPriceElement ||= this.card.querySelector('[data-full-price-cents]');
  }

  get quantity() {
    return parseInt(this.quantityElement.dataset.itemQuantity || 0);
  }

  get freeQuantity() {
    return parseInt(this.quantityElement.dataset.itemFreeQuantity || 0);
  }

  get unitPriceCents() {
    return Number(this.priceElement.dataset.unitPriceCents);
  }

  set unitPriceCents(unitPriceCents) {
    this.priceElement.dataset.unitPriceCents = unitPriceCents;
  }

  get originalUnitPriceCents() {
    return Number(this.priceElement.dataset.originalUnitPriceCents);
  }

  get totalPriceCents() {
    return Number(this.priceElement.dataset.totalPriceCents);
  }

  get fullPriceCents() {
    return Number(this.fullPriceElement.dataset.fullPriceCents || 0);
  }

  set fullPriceCents(fullPriceCents) {
    if (fullPriceCents) {
      this.fullPriceElement.dataset.fullPriceCents = fullPriceCents;
      this.fullPriceElement.innerText = MMSuperboxUtils.formatPrice(fullPriceCents / 100);
    } else { // clear and hide full price element
      this.fullPriceElement.dataset.fullPriceCents = "";
      this.fullPriceElement.innerText = null;
    }
  }

  set quantity(newQuantity) { // product.quantity = value;
    newQuantity = Math.ceil(newQuantity);
    if (newQuantity < 0) { newQuantity = 0 }
    this.quantityElement.dataset.itemQuantity = newQuantity;
    this.quantityElement.innerText = newQuantity;
    this.setTotalPrice();
  }

  setTotalPrice() {
    let quantity = (this.quantity - this.freeQuantity);
    if (quantity < 0) quantity = 0;
    const newTotalPriceCents = quantity * this.unitPriceCents;
    this.priceElement.dataset.totalPriceCents = newTotalPriceCents;
    this.priceElement.innerText = MMSuperboxUtils.formatPrice(newTotalPriceCents / 100);
    if(this.fullPriceElement && this.originalUnitPriceCents) {
      this.fullPriceCents = this.originalUnitPriceCents * quantity;
    }
  }

  get requiresContainer() {
    return this.quantity > 0 && !!this.containerHandle;
  }
}

class MMSuperboxProduct extends MMSuperboxBaseProduct {
  constructor(superbox, productData) {
    super();
    this.superbox = superbox;
    this.id = productData.id;
    this.title = productData.title;
    this.containerHandle = productData.container_handle;
    this.sellingPlanId = productData.selling_plan_id;
    this.sellingPlans = productData.selling_plans;
    this.weightByInhabitant = productData.weight_by_inhabitant;
    this.category = productData.type;
    this.categoryHandle = productData.type_handle;
    this.fragrance = productData.fragrance;
    this.fragranceHandle = productData.fragrance_handle;
    this.fragranceImgSrc = productData.fragrance_img_src;
  }

  // this._card ||= ... syntax is for memoization. First time card() method is called, it looks for the card
  // in productCardTargets and set the _card property. Next times, it just returns this._card.
  // It aims to reduce computation at runtime and repeatitive searches in DOM.
  get card() {
    this._card ||= this.superbox.step("products").productCardTargets.find((productCard) => {
      return parseInt(productCard.dataset.productId) === this.id;
    });
    return this._card;
  }

  get variantId() {
    return parseInt(this.card.dataset.cartVariantId);
  }

  set frequency(newFrequency) {
    const frequencyElement = this.card.querySelector('[data-mm-superbox-product-frequency]');
    frequencyElement.innerText = newFrequency;
  }

  get isDisplayed() {
    return this.card.style.display !== "none";
  }

  hideCard() {
    MMSuperboxUtils.hideElement(this.card);
  }

  displayCard() {
    MMSuperboxUtils.displayElement(this.card);
  }

  applyDiscount(discountRatio) {
    if (discountRatio) {
      const discountedUnitPrice = this.originalUnitPriceCents - (this.originalUnitPriceCents * discountRatio);
      this.unitPriceCents = discountedUnitPrice;
      this.fullPriceCents = this.originalUnitPriceCents;
    } else { // remove discount indicator
      this.fullPriceCents = null;
      this.unitPriceCents = this.originalUnitPriceCents;
    }
    this.setTotalPrice();
  }
}

class MMSuperboxContainerProduct extends MMSuperboxBaseProduct {
  constructor(superbox, containerData) {
    super();
    this.superbox = superbox;
    this.containerData = containerData;
    this.id = containerData.id;
    this.handle = containerData.handle;
    this.freeVariant = containerData.variants.find(variant => variant.price === 0);
    this.chargedVariant = containerData.variants.find(variant => variant.price > 0);
  }

  buildCard(freeQuantity) {
    const template = this.superbox.step("containers").containerTemplateTarget;
    this.card = template.content.cloneNode(true).firstElementChild;
    this.card.dataset.productId = this.containerData.id;

    const titleEl = this.card.querySelector("[data-container-title]")
    titleEl.innerText = this.containerData.title;
    const imgEl = this.card.querySelector("[data-container-img]");
    imgEl.src = this.containerData.featured_image;
    imgEl.alt = this.containerData.title;

    this.quantityElement.dataset.itemFreeQuantity = freeQuantity;
    this.priceElement.dataset.unitPriceCents = this.containerData.price_max;
    this.fullPriceElement.dataset.fullPriceCents = this.containerData.compare_at_price_max;

    return this.card;
  }

  get offeredQuantity() {
    return this.quantity < this.freeQuantity ? this.quantity : this.freeQuantity;
  }

  get chargedQuantity() {
    const quantity = this.quantity - this.freeQuantity;
    return quantity < 0 ? 0 : quantity;
  }
}

class MMSuperboxStep {
  constructor(superbox) {
    this.superbox = superbox;
    this.name = this.constructor.name;
    this.element = superbox.element.querySelector(`[data-mm-superbox-step="${this.name}"]`);
    this.index = null; // set in superbox constructor
  }

  get otherSteps() {
    return this.superbox.steps.filter(step => {
      return step.name !== this.name;
    });
  }

  get previousStep() {
    return this.superbox.findPreviousStep(this.index);
  }

  get nextStep() {
    return this.superbox.findNextStep(this.index);
  }

  get isLastStep() {
    return this.nextStep === null;
  }

  get isRequired() { return true }

  display() {
    this.otherSteps.forEach(step => step.hide());
    MMSuperboxUtils.displayElement(this.element);
  }

  hide() {
    MMSuperboxUtils.hideElement(this.element);
  }

  /* Triggered before step is displayed */
  prepare() {}
  /*
    Can be triggered when anytime.
    Common example is when an input's value changes :
    <input onchange="mmSuperbox.updateCurrentStep()" ...>
  */
  update() {}
  /*
    Triggered before going to next step. Exclusively called
    through `<button onclick="mmSuperbox.submitCurrentStep()" ...>`
  */
  submit() {}
  reset() {}
}

class MMSuperboxStepNeeds extends MMSuperboxStep {
  static name = "needs";
  static targets = ["needInput", "discountInfo", "submitBtn"]

  static discountInfos = [
    "0 gamme (bof...): <span>-0%</span>",
    "1 gamme (pas mal !): <span>-5%</span>",
    "2 gammes (hé hé !): <span>-10%</span>",
    "3 gammes (waouh !): <span>-15%</span>",
    "4 gammes (jackpot !): <span>-20%</span>"
  ]

  get selectedNeedInputs() {
    return this.needInputTargets.filter(needInput => needInput.checked);
  }

  prepare() {
    this.superbox.step("question").reset();
  }

  update() {
    const selectedNeedsCounter = this.selectedNeedInputs.length;
    this.discountInfoTarget.innerHTML = this.constructor.discountInfos[selectedNeedsCounter];
    this.submitBtnTarget.disabled = selectedNeedsCounter === 0;
  }

  reset() {
    this.submitBtnTarget.disabled = true;
    this.discountInfoTarget.innerHTML = this.constructor.discountInfos[0];
    this.needInputTargets.forEach(input => input.checked = false);
  }

  submit() {
    this.superbox.step("question").reset();
  }
}

class MMSuperboxStepInhabitants extends MMSuperboxStep {
  static name = "inhabitants";
  static targets = ["inhabitantsCountInput", "submitBtn"];

  getInHabitantsCountByTypology() {
    const countByTypology = {};
    this.inhabitantsCountInputTargets.forEach((input) => {
      countByTypology[input.name] = parseInt(input.value);
    });
    return countByTypology;
  }

  setInhabitantCount(typology, count) {
    const countInput = this.inhabitantsCountInputTargets.find(input => input.name === typology);

    countInput.value = count;
  }

  update() {
    const inhabitantsFilled = this.inhabitantsCountInputTargets.some((input) => {
      return parseInt(input.value) > 0;
    });
    this.submitBtnTarget.disabled = !inhabitantsFilled;
  }

  reset() {
    this.submitBtnTarget.disabled = true;
    this.inhabitantsCountInputTargets.forEach((input, index) => {
      input.value = 0;
    });
  }
}

class MMSuperboxStepQuestion extends MMSuperboxStep {
  static name = "question";
  static targets = ["questionContainer", "answerInput", "submitBtn"];

  get isRequired() {
    return this.superbox.requiresQuestion;
  }

  reset() {
    this.questionContainerTargets.forEach(MMSuperboxUtils.hideElement);
    this.answerInputTargets.forEach(input => input.checked = false);
    this.submitBtnTarget.disabled = true;
  }

  prepare() {
    const selectedQuestionHandles = this.superbox.selectedNeedsWithQuestion
                                        .map(need => need.questionHandle);

    this.questionContainerTargets.forEach((container) => {
      const { dataset: { questionHandle } } = container;
      const needIsSelected = selectedQuestionHandles.includes(questionHandle);
      if (needIsSelected) {
        return MMSuperboxUtils.displayElement(container);
      }
      MMSuperboxUtils.hideElement(container);
    });
  }

  update() {
    const allAnswerSelected = this.superbox.selectedNeedsWithQuestion.every(need => need.selectedAnswer);
    this.submitBtnTarget.disabled = !allAnswerSelected;
  }
}

class MMSuperboxStepFragrances extends MMSuperboxStep {
  static name = "fragrances";
  static targets = [
    "categoryTemplate",
    "fragranceChoiceTemplate",
    "categories",
    "category",
    "submitBtn"
  ];

  get isRequired() {
    return this.superbox.requiresFragrancesChoice;
  }

  reset() {
    this.categoriesTarget.innerHTML = "";
    this.submitBtnTarget.disabled = true;
    this.buildAndInsertChoices();
  }

  prepare() {
    if (!this.categoriesTarget.innerHTML) {
      this.buildAndInsertChoices();
    }
  }

  update() {
    const allChoicesMade = this.categoryTargets.every((categoryTarget) => {
      return Array.from(categoryTarget.querySelectorAll("input")).some(input => input.checked);
    });
    this.submitBtnTarget.disabled = !allChoicesMade;
  }

  buildAndInsertChoices() {
    this.superbox.selectedNeeds.forEach((need) => {
      if (need.productGroups) {
        need.productGroups.forEach((products) => {
          if (products.length > 1) {
            const categoryEl = this.categoryTemplateTarget.content.cloneNode(true).firstElementChild;
            const productWithcategory = products.find((product) => {
              return !!product.category;
            });
            if (!productWithcategory) { return }
            const { category, categoryHandle } = productWithcategory;

            categoryEl.dataset.categoryHandle = categoryHandle;
            categoryEl.querySelector("[data-category-title]").innerHTML = category;

            products.forEach((product) => {
              const optionEl = this.fragranceChoiceTemplateTarget.content.cloneNode(true).firstElementChild;
              const { fragrance, fragranceHandle } = product;
              const inputId = `${categoryHandle}-${fragranceHandle}`;
              optionEl.querySelector("label").htmlFor = inputId;
              optionEl.querySelector("[data-category-choice-label]").innerHTML = fragrance;
              optionEl.querySelector("[data-category-choice-img]").src = product.fragranceImgSrc;
              optionEl.querySelector("[data-category-choice-input]").name = categoryHandle;
              optionEl.querySelector("[data-category-choice-input]").value = fragranceHandle;
              optionEl.querySelector("[data-category-choice-input]").id = inputId;

              categoryEl.querySelector("[data-category-choices]").appendChild(optionEl);
            });

            this.categoriesTarget.appendChild(categoryEl);
          }
        });
      }
    });
  }
}

class MMSuperboxStepProducts extends MMSuperboxStep {
  static name = "products";
  static targets = [
    "needProductsContainer",
    "answerProductsContainer",
    "productCard",
    "submitBtn",
    "submitBtnMainText",
    "submitBtnPriceDetail",
    "catchphrase",
    "monthFrequencyInput"
  ];

  reset() {
    this.superbox.products.forEach(product => product.quantity = 0);
    this.needProductsContainerTargets.forEach(MMSuperboxUtils.hideElement);
    this.answerProductsContainerTargets.forEach(MMSuperboxUtils.hideElement);
    this.superbox.needs.forEach((need) => {
      if (need.requiresQuestion) {
        need.products.forEach((product) => product.hideCard());
      }
    });
  }

  prepare() {
    this.reset();
    this.prepareProducts();
    this.update();
  }

  prepareProducts() {
    const productsToAdd = [];
    const productGroupsWithQuantityToDistribute = []

    // update products prices according to discound for number of selected needs
    this.superbox.products.forEach(product => product.applyDiscount(this.superbox.currentDiscountRatio));

    this.superbox.selectedNeeds.forEach((need) => {
      const needProductsContainer = this.needProductsContainerTargets.find((container) => {
        return container.dataset.needHandle === need.handle;
      });
      MMSuperboxUtils.displayElement(needProductsContainer);

      if (need.requiresQuestion) {
        need.selectedAnswer.products.forEach(product => {
          product.displayCard();
          productsToAdd.push(product);
        });
      } else {
        const { selectedFragrancesByCategory } = this.superbox;
        need.productGroups.forEach((productGroup) => {
          if (productGroup.length > 1) {
            const selectedProducts = [];
            productGroup.forEach((product) => {
              const { categoryHandle, fragranceHandle } = product;

              if (selectedFragrancesByCategory[categoryHandle].includes(fragranceHandle)) {
                product.displayCard();
                selectedProducts.push(product);
              } else {
                product.hideCard();
              }
            });
            if (selectedProducts.length) {
              productGroupsWithQuantityToDistribute.push(selectedProducts);
            }
          } else {
            productsToAdd.push(productGroup[0]);
          }
        });
      }
    });

    const inhabitantsCountByTypology = this.superbox.getInHabitantsCountByTypology();
    const typologies = Object.keys(inhabitantsCountByTypology);
    const monthFrequency = this.superbox.selectedMonthFrequency;
    const quantityForProduct = (product) => {
      let quantity = 0;
      typologies.forEach((typology) => {
        quantity += product.weightByInhabitant[typology] * inhabitantsCountByTypology[typology] * monthFrequency;
      });
      return quantity;
    };

    productsToAdd.forEach(product => product.quantity = quantityForProduct(product));

    productGroupsWithQuantityToDistribute.forEach((products) => {
      let globalQuantity = quantityForProduct(products[0]);
      products.forEach((product, index) => {
        const remainingProducts = (products.length - index);
        const quantity = Math.ceil(globalQuantity / remainingProducts);
        globalQuantity -= quantity;
        product.quantity = quantity;
      });
    });
  }

  updateFrequencyDisplay() {
    const monthFrequency = this.superbox.selectedMonthFrequency;
    const discountPercent = this.superbox.currentDiscountPercent;
    this.catchphraseTargets.forEach((catchphraseTarget) => {
      catchphraseTarget.innerHTML = `<strong>${monthFrequency}</strong> mois de tranquillité et <strong>${discountPercent}%</strong> économisés`;
    });
    this.superbox.products.forEach((product) => {
      product.frequency = monthFrequency;
    })
  }

  update() {
    let totalPriceCents = 0;
    let totalFullPriceCents = 0;
    this.superbox.products.forEach((product) => {
      totalPriceCents += product.totalPriceCents;
      totalFullPriceCents += product.fullPriceCents;
    });
    const totalPrice = MMSuperboxUtils.formatPrice(totalPriceCents / 100);
    const totalFullPrice = MMSuperboxUtils.formatPrice(totalFullPriceCents / 100);
    MMSuperboxUtils.addToElementText(this.submitBtnMainTextTarget, ` (+ ${totalPrice})`);
    this.submitBtnPriceDetailTarget.innerText = `au lieu de ${totalFullPrice}`;

    this.submitBtnTarget.disabled = totalPriceCents === 0;
    this.updateFrequencyDisplay();
  }
}

class MMSuperboxStepContainers extends MMSuperboxStep {
  static name = "containers";
  static targets = [
    "containerTemplate",
    "containers",
    "loader",
    "containerCard",
    "submitBtn"
  ];

  get isRequired() {
    return this.superbox.requiresContainers;
  }

  reset() {
    this.submitBtnTarget.disabled = false; // btn is disabled when final submit, to prevent accidental double submission
    MMSuperboxUtils.displayElement(this.loaderTarget);
    this.containersTarget.innerHTML = null;
    this.containers = null;
    MMSuperboxUtils.addToElementText(this.submitBtnTarget, ` (+ ${MMSuperboxUtils.formatPrice(0)})`);
  }

  fetchContainersData(containerHandles) {
    return Promise.all(containerHandles.map((containerHandle) => {
      return mmAPIRequest('GET', `products/${containerHandle}.js`);
    }));
  }

  prepare() {
    this.reset();
    const containersQuantityByHandle = {};
    const addedCategories = new Set();
    const addedTuples = [];
    this.superbox.products.forEach((product) => {
      if (product.requiresContainer) {
        const tuple = [product.categoryHandle, product.containerHandle];

        const hasTuple = addedTuples.find(([category, container]) => {
          return category === tuple[0] && container === tuple[1];
        });

        if (product.categoryHandle && hasTuple) { return }
        else if (product.categoryHandle) { addedTuples.push(tuple) }

        // if (product.categoryHandle && addedCategories.has(tuple)) { return }
        // else if (product.categoryHandle) { addedCategories.add(tuple) }
        // if (product.containerHandle && addedCategories.has(product.containerHandle)) { return }
        // else if (product.containerHandle) { addedCategories.add(product.containerHandle) }

        containersQuantityByHandle[product.containerHandle] ||= 0;
        containersQuantityByHandle[product.containerHandle] += 1;
      }
    });
    console.log(addedTuples)
    const containerHandles = Object.keys(containersQuantityByHandle);
    this.fetchContainersData(containerHandles)
      .then((containersData) => {
        MMSuperboxUtils.hideElement(this.loaderTarget);
        this.containers = containersData.map(containerData => {
          const container = new MMSuperboxContainerProduct(this.superbox, containerData);
          container.buildCard(containersQuantityByHandle[container.handle]);
          container.quantity = containersQuantityByHandle[container.handle];
          this.containersTarget.appendChild(container.card);
          return container;
        });
      });
  }

  update() {
    let totalPriceCents = 0;
    this.containers.forEach(container => totalPriceCents += container.totalPriceCents);
    MMSuperboxUtils.addToElementText(this.submitBtnTarget, ` (+ ${MMSuperboxUtils.formatPrice(totalPriceCents / 100)})`);
  }
}

class MMSuperboxCart {
  constructor(superbox) {
    this.superbox = superbox;
  }

  async getCart() { return await mmAPIRequest('GET', 'cart.js') }

  async clearExistingSuperbox() {
    const cart = await this.getCart();
    const itemsToRemove = {};
    cart.items.forEach((item) => {
      if (item.properties) {
        if (item.properties.__superpack) { itemsToRemove[item.key] = 0 }
      }
    });
    return await mmAPIRequest(
      'POST', 'cart/update.js',
      { updates: itemsToRemove, attributes: { superpack_needs: null } }
    );
  }

  buildContainersItems() {
    const containers = this.superbox.containers || [];
    const lineItems = [];
    containers.forEach((container) => {
      if (container.offeredQuantity) {
        lineItems.push({
          id: container.freeVariant.id, quantity: container.offeredQuantity,  properties: { "__superpack": true }
        })
      }
      if (container.chargedQuantity) {
        lineItems.push({
          id: container.chargedVariant.id, quantity: container.chargedQuantity, properties: { "__superpack": true }
        })
      }
    });
    return lineItems;
  }

  addProductsToCart() {
    const submitToPath = `/discount/${this.superbox.currentDiscountCode}?redirect=/checkout`;
    const items = this.buildContainersItems();

    this.superbox.products.forEach((product) => {
      if (product.quantity > 0) {
        const selectedMonthFrequency = this.superbox.selectedMonthFrequency;
        const sellingPlan = product.sellingPlans.find(sellingPlan => sellingPlan.name.includes(selectedMonthFrequency));
        const sellingPlanId = sellingPlan.id;

        items.push({
          quantity: product.quantity,
          id: product.variantId,
          properties: { "__superpack": true },
          selling_plan: sellingPlanId
        });
      }
    });

    return mmAPIRequest('POST', 'cart/add.js', { items, sections: "mm-cart" })
      .then(refreshCartFromData)
      .then(async (data) => {
        await this.saveSelectedNeeds();
        this.superbox.close();
        this.superbox.reset();
        setTimeout(() => window.location.href = submitToPath);
      }).catch((error) => {
        mmCartProductsContainerElement().innerHTML = 'Oups, il y a eu une erreur, essayez de recharger la page !';
        mmOpenCart();
        console.log(error);
      })
  }

  async saveSelectedNeeds() {
    const answersByNeedHandle = {};
    this.superbox.selectedNeeds.forEach((need) => {
      if (need.requiresQuestion) { answersByNeedHandle[need.handle] = need.selectedAnswer.handle }
    });
    const state = {
      selectedNeedHandles: this.superbox.selectedNeeds.map(need => need.handle),
      inhabitantsCountByTypology: this.superbox.getInHabitantsCountByTypology(),
      selectedFrequency: this.superbox.selectedMonthFrequency,
      selectedFragrancesByCategory: this.superbox.selectedFragrancesByCategory,
      answersByNeedHandle
    }
    await mmAPIRequest(
      'POST', 'cart/update.js', {
        attributes: {
          superbox_state: JSON.stringify(state)
        }
      }
    );
  }
}
